import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { useUrlState } from '../hooks/useUrlState';
import { useAuth } from '../hooks/useAuth';
import { api, ForecastUserRow, User } from '../lib/api';
import { fmtCurrency, fmtMonth } from '../lib/fmt';
import { downloadCsv } from '../lib/csv';
import {
  ActionButton, SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge,
  MetricCard, Select, Spinner, EmptyState,
} from '../components/ui';
import { ChartCard, ForecastChart } from '../components/charts';

const HORIZON_OPTIONS = ['1', '3', '6'];

export default function AdminForecastPage() {
  const [horizonStr, setHorizonStr] = useUrlState('months', '3');
  const horizon = parseInt(horizonStr) || 3;
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(
    () => api.admin.forecast(horizon),
    [horizon],
  );

  const months = data?.months ?? [];
  const users = data?.users ?? [];
  const pool = data?.pool_by_month ?? [];

  // Sort by projected total in the current month (desc).
  const sorted: ForecastUserRow[] = useMemo(() => {
    if (!months.length) return users;
    const currentMonth = months[0];
    return [...users].sort((a, b) => {
      const aTotal = a.months.find((m) => m.attribution_month === currentMonth)?.projected_total ?? 0;
      const bTotal = b.months.find((m) => m.attribution_month === currentMonth)?.projected_total ?? 0;
      return bTotal - aTotal;
    });
  }, [users, months]);

  async function viewUser(u: ForecastUserRow['user']) {
    try {
      await startImpersonation({
        ...u,
        manager_jira_user_id: null,
        is_admin: false,
        is_active: true,
      } as User);
      navigate('/scorecard');
    } catch (e) {
      alert(`Could not view: ${(e as Error).message}`);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Bonus forecast</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <ActionButton
            onClick={() => {
              if (!sorted.length) return;
              const rows: Array<Array<unknown>> = [
                ['Employee', 'Role', ...months.map((m) => `${fmtMonth(m)} — actual`), ...months.map((m) => `${fmtMonth(m)} — projected`)],
              ];
              for (const r of sorted) {
                const actuals = months.map((m) => r.months.find((x) => x.attribution_month === m)?.actual_shipped.toFixed(2) ?? '');
                const projected = months.map((m) => r.months.find((x) => x.attribution_month === m)?.projected_total.toFixed(2) ?? '');
                rows.push([r.user.display_name, r.user.role, ...actuals, ...projected]);
              }
              downloadCsv(`bonus-forecast-${horizonStr}mo.csv`, rows);
            }}
            variant="default"
            disabled={!sorted.length}
          >
            Export CSV
          </ActionButton>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Horizon</span>
          <Select value={horizonStr} onChange={setHorizonStr}>
            {HORIZON_OPTIONS.map((h) => (
              <option key={h} value={h}>{h} month{h === '1' ? '' : 's'}</option>
            ))}
          </Select>
        </span>
      </div>

      {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : !data ? null : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${months.length}, minmax(0,1fr))`, gap: 10 }}>
            {pool.map((p) => (
              <MetricCard
                key={p.attribution_month}
                label={`Bonus pool — ${fmtMonth(p.attribution_month)}`}
                value={fmtCurrency(p.total_projected_bonus)}
                accent={p.total_projected_bonus > 0 ? 'green' : undefined}
                sub={`${p.users_on_tier} / ${p.total_users} on tier`}
              />
            ))}
          </div>

          {/* Trend across the forecast horizon: actual-shipped so far (green)
              + projected for still-open pipeline (amber). Lets execs eyeball
              which months are front-loaded vs back-loaded. */}
          {months.length > 0 && (
            <ChartCard title="Shipped + projected by month" sub={`${months.length}-month horizon`} height={200}>
              <ForecastChart
                data={months.map((m) => {
                  // Sum across all users for that month.
                  let actual = 0;
                  let projected = 0;
                  for (const u of users) {
                    const fm = u.months.find((x) => x.attribution_month === m);
                    if (!fm) continue;
                    actual    += fm.actual_shipped;
                    projected += Math.max(0, fm.projected_total - fm.actual_shipped);
                  }
                  return { month: fmtMonth(m), actual: Math.round(actual), projected: Math.round(projected) };
                })}
              />
            </ChartCard>
          )}

          <Card>
            <CardHeader>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Per-user forecast</span>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                {sorted.length} employees · sorted by current-month projected total
              </span>
            </CardHeader>
            {sorted.length === 0 ? (
              <EmptyState message="No bonus-eligible employees." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Employee</Th>
                    <Th>Role</Th>
                    {months.map((m) => (
                      <Th key={m} right>{fmtMonth(m)}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row) => (
                    <Tr key={row.user.jira_user_id}>
                      <Td>
                        <button
                          onClick={() => viewUser(row.user)}
                          style={{
                            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            color: 'var(--color-blue)', textDecoration: 'underline',
                            fontWeight: 500, fontSize: 'inherit', textAlign: 'left',
                          }}
                          title="View this user's scorecard"
                        >
                          {row.user.display_name}
                        </button>
                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{row.user.title || '—'}</div>
                      </Td>
                      <Td><Badge variant="gray">{row.effective_bonus_role}</Badge></Td>
                      {months.map((m) => {
                        const fm = row.months.find((rm) => rm.attribution_month === m);
                        if (!fm) return <Td key={m} right muted>—</Td>;
                        const isCoaching = fm.gap_to_next_tier != null && fm.gap_to_next_tier < (fm.projected_total * 0.15 || 5000);
                        return (
                          <Td
                            key={m}
                            right
                            style={{
                              background: isCoaching ? 'var(--color-amber-bg)' : undefined,
                            }}
                          >
                            <div style={{ fontWeight: 500, color: fm.projected_total > 0 ? 'var(--color-green)' : undefined }}>
                              {fmtCurrency(fm.projected_total)}
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 1 }}>
                              {fm.projected_tier
                                ? <Badge variant="green">T{fm.projected_tier}</Badge>
                                : <Badge variant="gray">—</Badge>}
                              {fm.projected_bonus ? ` ${fmtCurrency(fm.projected_bonus)}` : ''}
                            </div>
                            {fm.actual_shipped > 0 && (
                              <div style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>
                                {fmtCurrency(fm.actual_shipped)} shipped + {fmtCurrency(fm.wip_due)} WIP
                              </div>
                            )}
                            {isCoaching && fm.gap_to_next_tier != null && (
                              <div style={{ fontSize: 10, color: 'var(--color-amber-text)', fontWeight: 500 }}>
                                {fmtCurrency(fm.gap_to_next_tier)} to next tier
                              </div>
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
            Forecast assumes all WIP planned for a given month (by due date) ships in that month.
            Highlighted rows = user is within 15% of the next tier threshold — coaching opportunity.
            Click a name to view their full scorecard.
          </div>
        </>
      )}
    </>
  );
}
